import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  CheckCircle2,
  Clock,
  Smile,
  ChevronRight,
  Phone,
  MessageSquare,
  MessagesSquare,
  Mail,
  BarChart3,
  Calendar,
  Bot,
  ArrowRight,
  Target,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, Badge } from './ui';
import { logsAPI, scriptsAPI, timeAgo, formatDuration, effectiveDurationMs, logChannel, scriptKind } from './api';
import { LoadingRows, ErrorNote } from './DemoCustomerAIPages';

/* ---------- small building blocks ---------- */

const Sparkline = ({ points, color }) => {
  if (!points || points.length < 2) return <span className="w-24" />;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const norm = points.map((p, i) => {
    const x = (i / (points.length - 1)) * 100;
    const y = 28 - ((p - min) / (max - min || 1)) * 24 - 2;
    return `${x},${y}`;
  });
  return (
    <svg viewBox="0 0 100 30" className="h-8 w-24" preserveAspectRatio="none">
      <polyline
        points={norm.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const CardHeader = ({ title, action }) => (
  <div className="flex items-center justify-between px-6 pt-5">
    <h2 className="text-[16px] font-semibold">{title}</h2>
    {action}
  </div>
);

const AGENT_TINTS = [
  { tint: 'bg-teal-50 text-teal-600', spark: '#0D9488' },
  { tint: 'bg-violet-50 text-violet-600', spark: '#7C3AED' },
  { tint: 'bg-sky-50 text-sky-600', spark: '#0284C7' },
  { tint: 'bg-orange-50 text-orange-500', spark: '#F97316' },
];

const CHANNEL_META = {
  phone: { name: 'Voice', icon: Phone, tint: 'bg-teal-50 text-teal-600' },
  sms: { name: 'SMS', icon: MessageSquare, tint: 'bg-violet-50 text-violet-600' },
  chat: { name: 'Chat', icon: MessagesSquare, tint: 'bg-sky-50 text-sky-600' },
  email: { name: 'Email', icon: Mail, tint: 'bg-orange-50 text-orange-500' },
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/* ---------- page ---------- */

const DemoHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [logs, setLogs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([logsAPI.usage(), scriptsAPI.list(), logsAPI.list()])
      .then(([usageRes, scriptsRes, logsRes]) => {
        setUsage(usageRes.data.stats || {});
        setScripts(scriptsRes.data.scripts || []);
        setLogs(logsRes.data.logs || []);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const firstName = (user?.name || user?.email || 'there').split(/[\s@]/)[0];

  const kpis = useMemo(() => {
    if (!usage) return [];
    const oc = usage.outcomeCounts || {};
    const mc = usage.moodCounts || {};
    const analyzed = (oc.success || 0) + (oc.failure || 0);
    const moodTotal = (mc.positive || 0) + (mc.neutral || 0) + (mc.negative || 0);
    return [
      { label: 'Conversations', value: (usage.totalCalls || 0).toLocaleString(), icon: MessageCircle, tint: 'bg-teal-50 text-teal-600' },
      { label: 'Successful', value: (oc.success || 0).toLocaleString(), icon: CheckCircle2, tint: 'bg-emerald-50 text-emerald-600' },
      {
        label: 'Resolution Rate',
        value: analyzed ? `${Math.round(((oc.success || 0) / analyzed) * 100)}%` : '—',
        icon: Target,
        tint: 'bg-sky-50 text-sky-500',
      },
      {
        label: 'Avg. Duration',
        value: usage.averageDurationMinutes ? `${Number(usage.averageDurationMinutes).toFixed(1)}m` : '—',
        icon: Clock,
        tint: 'bg-orange-50 text-orange-500',
      },
      {
        label: 'Positive Mood',
        value: moodTotal ? `${Math.round(((mc.positive || 0) / moodTotal) * 100)}%` : '—',
        icon: Smile,
        tint: 'bg-violet-50 text-violet-600',
      },
    ];
  }, [usage]);

  const dailySeries = useMemo(() => {
    const daily = usage?.dailyCalls || {};
    return Object.keys(daily)
      .sort()
      .slice(-14)
      .map((k) => daily[k]);
  }, [usage]);

  const topAgents = useMemo(() => {
    const counts = usage?.scriptCounts || {};
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([name, count], i) => {
        const script = scripts.find((s) => s.scriptName === name);
        return { name, count, script, ...AGENT_TINTS[i % AGENT_TINTS.length] };
      });
  }, [usage, scripts]);

  const channelSummary = useMemo(() => {
    if (!logs) return [];
    const counts = { phone: 0, sms: 0, chat: 0, email: 0 };
    logs.forEach((l) => {
      counts[logChannel(l)] += 1;
    });
    return Object.entries(CHANNEL_META).map(([key, meta]) => ({ ...meta, value: counts[key] }));
  }, [logs]);

  const recentActivity = useMemo(() => (logs || []).slice(0, 20), [logs]);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Greeting */}
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3 pt-3">
        <div className="min-w-0">
          <h1 className="text-[26px] font-bold tracking-tight">
            {greeting()}, {firstName} <span className="align-middle">👋</span>
          </h1>
          <p className="mt-1 text-[14px] text-slate-500">
            Here's what's happening with your customer operations today.
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/customers/calendar')}
          title="Open calendar"
          className="flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-medium shadow-sm hover:bg-slate-50"
        >
          <Calendar size={16} className="text-slate-400" />
          {new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          <ArrowRight size={14} className="text-slate-400" />
        </button>
      </div>

      {error && <div className="mt-6"><ErrorNote error={error} /></div>}
      {!error && (usage === null || logs === null) && <LoadingRows label="Loading your dashboard…" />}

      {!error && usage !== null && logs !== null && (
        <div className="demo-scroll mt-4 min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-4 pr-3">
          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 xl:grid-cols-5">
            {kpis.map(({ label, value, icon: Icon, tint }) => (
              <Card key={label} className="p-4">
                <div className="flex items-center gap-2.5">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tint}`}>
                    <Icon size={18} strokeWidth={1.9} />
                  </span>
                  <span className="min-w-0 truncate text-[13px] font-medium text-slate-500">{label}</span>
                </div>
                <div className="mt-2.5 text-[24px] font-bold tracking-tight">{value}</div>
              </Card>
            ))}
          </div>

          {/* Middle row */}
          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_1fr]">
            {/* Active Customer AI */}
            <Card>
              <CardHeader
                title="Active Customer AI"
                action={
                  <button
                    onClick={() => navigate('/dashboard/customer-ai/scripts')}
                    className="text-[13px] font-medium text-teal-700 hover:text-teal-800"
                  >
                    View all
                  </button>
                }
              />
              <div className="mt-2 divide-y divide-slate-100 px-6">
                {topAgents.length === 0 && (
                  <p className="py-8 text-center text-[13.5px] text-slate-400">
                    No call activity yet — your busiest scripts will appear here.
                  </p>
                )}
                {topAgents.map(({ name, count, script, tint, spark }) => (
                  <div
                    key={name}
                    onClick={() => script && navigate(`/dashboard/customer-ai/scripts/${script._id}`)}
                    className={`flex items-center gap-4 py-4 ${script ? 'cursor-pointer hover:bg-slate-50/60' : ''}`}
                  >
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                      <Bot size={21} strokeWidth={1.9} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-[14px] font-semibold">{name}</span>
                        {script && (
                          <Badge tone={scriptKind(script.type) === 'Workflow' ? 'purple' : 'blue'}>
                            {scriptKind(script.type)}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[12.5px] text-slate-500">
                        {script ? `Created ${timeAgo(script.createdAt)}` : 'Script no longer exists'}
                      </p>
                    </div>
                    <div className="hidden text-right sm:block">
                      <div className="text-[17px] font-bold leading-tight">{count.toLocaleString()}</div>
                      <div className="text-[12px] text-slate-500">Conversations</div>
                    </div>
                    <Sparkline points={dailySeries} color={spark} />
                    <ChevronRight size={17} className="shrink-0 text-slate-300" />
                  </div>
                ))}
              </div>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader
                title="Recent Activity"
                action={
                  <button
                    onClick={() => navigate('/dashboard/logs')}
                    className="text-[13px] font-medium text-teal-700 hover:text-teal-800"
                  >
                    View all
                  </button>
                }
              />
              <div className="divide-y divide-slate-100 px-6 pb-2">
                {recentActivity.length === 0 && (
                  <p className="py-8 text-center text-[13.5px] text-slate-400">No conversations yet.</p>
                )}
                {recentActivity.map((log) => (
                  <div
                    key={log._id}
                    onClick={() => navigate('/dashboard/logs')}
                    className="flex cursor-pointer items-center gap-3.5 py-3.5 hover:bg-slate-50/60"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                      <MessageCircle size={17} strokeWidth={1.9} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13.5px] font-semibold">{log.scriptName || 'Conversation'}</p>
                      <p className="mt-0.5 truncate text-[12px] text-slate-500">
                        {log.summary || log.callType || 'Call'} • {formatDuration(effectiveDurationMs(log))}
                      </p>
                    </div>
                    <span className="shrink-0 text-[12px] text-slate-400">{timeAgo(log.createdAt)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Channel Summary */}
          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_1fr]">
            <Card>
              <CardHeader title="Channel Summary" />
              <div className="grid grid-cols-4 gap-3 px-6 py-5">
                {channelSummary.map(({ name, value, icon: Icon, tint }) => (
                  <div key={name} className="flex flex-col items-center gap-1.5">
                    <span className={`flex h-10 w-10 items-center justify-center rounded-full ${tint}`}>
                      <Icon size={18} strokeWidth={1.9} />
                    </span>
                    <span className="text-[12.5px] font-medium text-slate-500">{name}</span>
                    <span className="text-[18px] font-bold">{value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 py-3.5 text-center">
                <button
                  onClick={() => navigate('/dashboard/logs')}
                  className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-teal-700 hover:text-teal-800"
                >
                  View conversations <ArrowRight size={15} />
                </button>
              </div>
            </Card>

            {/* Calls over time */}
            <Card className="pb-5">
              <CardHeader title="Calls Over Time" />
              <div className="px-6 pt-4">
                {dailySeries.length >= 2 ? (
                  <svg viewBox="0 0 100 30" className="h-32 w-full" preserveAspectRatio="none">
                    <polyline
                      points={dailySeries
                        .map((v, i) => {
                          const max = Math.max(...dailySeries);
                          const min = Math.min(...dailySeries);
                          const x = (i / (dailySeries.length - 1)) * 100;
                          const y = 28 - ((v - min) / (max - min || 1)) * 24 - 2;
                          return `${x},${y}`;
                        })
                        .join(' ')}
                      fill="none"
                      stroke="#0D9488"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                ) : (
                  <p className="py-10 text-center text-[13.5px] text-slate-400">
                    Not enough history to chart yet.
                  </p>
                )}
                <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-400">
                  <span>Last {dailySeries.length} days</span>
                  <span className="flex items-center gap-1.5">
                    <BarChart3 size={12} /> {usage.totalCalls || 0} total calls
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoHome;
